import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, Shield, MapPin, Database, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../mock';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';

const iconMap = {
  Server: Server,
  Shield: Shield,
  MapPin: MapPin,
  Database: Database
};

const Services = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0', 'scale-100');
            entry.target.classList.remove('opacity-0', 'translate-y-24', 'scale-95');
          } else {
            entry.target.classList.remove('opacity-100', 'translate-y-0', 'scale-100');
            entry.target.classList.add('opacity-0', 'translate-y-24', 'scale-95');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.service-card-animate');
    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  const handleServiceClick = (serviceSlug) => {
    navigate(`/services/${serviceSlug}`);
    window.scrollTo(0, 0);
  };

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-[1.15]">
            <span className="bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent pb-2">
              {language === 'id' ? t(language, 'services.title') : 'Integrated IT Solutions'}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {t(language, 'services.description')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="service-card-animate opacity-0 translate-y-24 scale-95 transition-all duration-[1500ms] ease-out h-full"
                style={{ transitionDelay: `${index * 300}ms`, transitionTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
              >
                <div
                  className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group cursor-pointer border border-gray-100 hover:border-yellow-200 h-full flex flex-col hover:-translate-y-2"
                  onClick={() => handleServiceClick(service.slug)}
                >
                  {/* Service Image */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform">
                        <Icon className="text-yellow-600" size={22} />
                      </div>
                      <h3 className="text-lg font-bold text-white leading-snug">
                        {service.title}
                      </h3>
                    </div>
                  </div>

                  {/* Service Content */}
                  <div className="p-6 lg:p-7 flex flex-col h-full">
                    <p className="text-sm text-gray-600 mb-6 leading-relaxed font-medium">
                      {language === 'en' && service.descriptionEn ? service.descriptionEn : service.description}
                    </p>

                    {/* Features List */}
                    <div className="mb-6 flex-grow">
                      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-3">
                        {language === 'en' ? 'Key Features' : 'Fitur Utama'}
                      </div>
                      <div className="grid grid-cols-1 gap-2.5">
                        {(language === 'en' && service.featuresEn ? service.featuresEn : service.features).map((feature, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-2.5 rounded-lg border border-gray-100 bg-gradient-to-br from-white to-gray-50 px-2.5 py-2 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-md bg-yellow-50 text-yellow-600">
                              <CheckCircle2 size={14} />
                            </div>
                            <span className="text-xs text-gray-700 leading-snug">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleServiceClick(service.slug);
                      }}
                      className="w-full text-white font-semibold transition-all text-sm py-5 hover:brightness-110"
                      style={{ background: 'linear-gradient(56deg, rgba(13, 41, 67, 1) 0%, rgba(25, 54, 112, 1) 100%)' }}
                    >
                      {t(language, 'services.viewFullDetails')}
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;

